import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Typography, Divider } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { CompanyDto, CompanyRatesDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatCurrency } from 'utils'
import RatesAddEditDialog from './RatesAddEditDialog'
import { useAuth } from 'context'

interface RatesCardAddEditDialogProps {
  company: CompanyDto
}

export default function RatesListCard({ company }: RatesCardAddEditDialogProps) {
  const { permissions } = useAuth()

  const [isRatesSaving, setIsRatesSaving] = useState<boolean>(false)
  const [ratesAddEditDialogOpen, setRatesAddEditDialogOpen] = useState<boolean>(false)
  const [companyRates, setCompanyRates] = useState<CompanyRatesDto>(new CompanyRatesDto())
  const [isGettingRates, setIsGettingRates] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingRates(true)
      controllers
        .getCompanyRates(company.companyGuid)
        .then(response => {
          setCompanyRates(response.value)
        })
        .finally(() => {
          setIsGettingRates(false)
        })
    }
  }, [company])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <RatesAddEditDialog
          initialValues={companyRates}
          loading={isRatesSaving}
          onClose={() => {
            setRatesAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsRatesSaving(true)
            controllers
              .updateCompanyRates(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Rates Saved Successfully!', { variant: 'success' })
                setCompanyRates(response.value)
                setRatesAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsRatesSaving(false)
              })
          }}
          open={ratesAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Rates
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    onClick={() => {
                      setIsRatesSaving(false)
                      setRatesAddEditDialogOpen(true)
                    }}
                    variant='text'
                    endIcon={<Edit />}
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingRates && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingRates && (
              <>
                <Grid item xs={12}>
                  <LabelField
                    label='Import Charge'
                    value={companyRates.ratesImportCharge ? formatCurrency(companyRates.ratesImportCharge) : undefined}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Incoming Transportation'
                    value={
                      companyRates.ratesIncomingTransportation
                        ? formatCurrency(companyRates.ratesIncomingTransportation)
                        : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Cluster Conversion'
                    value={
                      companyRates.ratesClusterConversion ? formatCurrency(companyRates.ratesClusterConversion) : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Cluster Programming'
                    value={
                      companyRates.ratesClusterProgramming ? formatCurrency(companyRates.ratesClusterProgramming) : undefined
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Recall Expenses'
                    value={companyRates.ratesRecallExpenses ? formatCurrency(companyRates.ratesRecallExpenses) : undefined}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Title Fees'
                    value={companyRates.ratesTitleFees ? formatCurrency(companyRates.ratesTitleFees) : undefined}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LabelField
                    label='Outbound Transportation'
                    value={
                      companyRates.ratesOutboundTransportation
                        ? formatCurrency(companyRates.ratesOutboundTransportation)
                        : undefined
                    }
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                ||This card only shows if the company is marked as an Exporter Company and/or Buyer Company. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
